import { useCallback, useEffect, useRef, useState } from 'react';
import InlineLoadError from '../components/InlineLoadError';
import { Link, useSearchParams } from '../routing';
import { categoryLinesApi, settingsApi } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { useSettings } from '../context/SettingsContext';
import { toast } from '../components/Toast';
import Icon from '../components/Icon';
import CategoryLineDefinitionSheet from '../components/CategoryLineDefinitionSheet';
import BudgetTargetsSheet from '../components/BudgetTargetsSheet';
import BudgetMappingPanel from '../components/BudgetMappingPanel';
import RevenueEntrySheet from '../components/RevenueEntrySheet';
import HqOnlyNotice from '../components/HqOnlyNotice';
import PageViewAsMenu from '../components/ViewAsMenu';

// הוצאות והכנסות — the management screen for the budget module: define the
// expense lines, wire what feeds them, set their targets, and enter revenue.
//
// Four sections, in workflow order (spec §3):
//   1. הגדרת קטגוריות   — identity only (CategoryLineDefinitionSheet)
//   2. שיוך סיווגים וספקים — BudgetMappingPanel, functionally unchanged
//   3. יעדי תקציב           — targets + the annual coverage strip
//   4. הכנסות               — revenue entries (RevenueEntrySheet)
//
// A separate screen from /item-classifications on purpose. Tagging is hundreds of
// rows worked through by staff; budgeting is 3-10 rows set monthly by an owner.
// Reporting still lives on /analysis — this page reports nothing.

// This screen is reached FROM ניתוח רכש — it is not a nav item — so without a
// way back the only exit is the browser button. Same reasoning, and the same
// markup, as CatalogBreadcrumb on /item-classifications, the other card-reached
// screen. Rendered on EVERY path below, including loading and error: those are
// exactly the states a user most wants to leave, and they are the ones where a
// breadcrumb is most often forgotten.
function BudgetBreadcrumb() {
  return (
    <div className="breadcrumb">
      <Link className="link" to="/analysis">ניתוח רכש</Link>
      <Icon name="chevron-inline" size={13} className="breadcrumb-sep" />
      <span>ניהול קטגוריות</span>
    </div>
  );
}

export default function BudgetPage() {
  const { restaurantId, canEdit } = useAuth();
  // "מודול הוצאות והכנסות" — module off renders a message card, NOT a redirect.
  // A deliberate deviation from PaymentsPage.jsx's redirect pattern: the flag
  // defaults false until settings load, so a redirect would bounce every
  // direct visit to /budget during that window; a card just resolves in place.
  const { budgetModuleEnabled, vatRate } = useSettings();

  const [lines, setLines] = useState([]);
  const [summary, setSummary] = useState(null);
  const [settings, setSettings] = useState(null);
  const [mappings, setMappings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  // The targets sheet can be jumped to a specific period from its coverage
  // strip; null = the current period (the summary endpoint's default).
  const [periodKey, setPeriodKey] = useState(null);

  // `?definitions=1` opens the business-category definitions from the revenue menu.
  // Arrival intents from the two /analysis cards: `?targets=1` (the "קבע יעדי
  // הוצאה" CTA) opens the targets sheet; `?revenue=1` (the coverage banner /
  // "ניהול הכנסות" link) opens the revenue section. Mirrors `?untagged=1` on
  // /item-classifications, the other card-reached screen with an arrival intent.
  const [searchParams] = useSearchParams();
  const arrivedForTargets = searchParams.get('targets') === '1';
  const arrivedForRevenue = searchParams.get('revenue') === '1';
  const arrivedForDefinitions = searchParams.get('definitions') === '1';
  // Otherwise collapsed by default: definitions and targets are set once a
  // month, while the mapping queue and revenue entry are the recurring work.
  const [defOpen, setDefOpen] = useState(arrivedForDefinitions);
  // Open by default (owner): unlike definitions/targets, mapping is the
  // recurring queue people land here to work — and even collapsed, the
  // headline + bar stay visible (see BudgetMappingPanel) so the one number
  // that matters ("how much is still unmapped") never needs a click to see.
  //
  // EXCEPT on a `?revenue=1`, `?targets=1` or `?definitions=1` arrival: those intents each ask
  // for exactly one thing — entering a Z-report, or setting a budget target —
  // and landing with the mapping queue also sprung open buried whichever
  // section the user actually asked for below an unrelated card.
  const [mappingOpen, setMappingOpen] = useState(!arrivedForRevenue && !arrivedForTargets && !arrivedForDefinitions);
  const [targetsOpen, setTargetsOpen] = useState(arrivedForTargets);
  const [revenueOpen, setRevenueOpen] = useState(arrivedForRevenue);
  // Scrolled to once, after the revenue section has actually mounted —
  // arrivedForRevenue never changes post-mount, so a ref (not state) tracks
  // whether that has already happened.
  const revenueScrolledRef = useRef(false);
  const revenueSectionRef = useRef(null);
  // Which single mapping row is saving. Gating the whole panel froze a list
  // someone is working down, 37 times in a row.
  const [savingId, setSavingId] = useState(null);

  const loadLines    = useCallback(() => categoryLinesApi.getAll().then(r => setLines(r.data || [])), []);
  const loadSummary  = useCallback((key) => categoryLinesApi.summary({
    with_previous: 1, ...(key ? { period_key: key } : {}),
  }).then(r => setSummary(r.data)), []);
  const loadMappings = useCallback(() => categoryLinesApi.mappings().then(r => setMappings(r.data)), []);
  const loadSettings = useCallback(() => settingsApi.getTargets().then(r => setSettings(r.data)).catch(() => setSettings(null)), []);

  // Named so the failure state can offer a retry — an inline effect body has
  // nothing to hand InlineLoadError's onRetry.
  const reload = useCallback(() => {
    if (!budgetModuleEnabled) return;
    setLoading(true);
    setLoadError('');
    Promise.all([loadLines(), loadSummary(null), loadMappings(), loadSettings()])
      .catch(e => setLoadError(e?.response?.data?.error || 'שגיאה בטעינת התקציב'))
      .finally(() => setLoading(false));
  }, [loadLines, loadSummary, loadMappings, loadSettings, budgetModuleEnabled]);

  useEffect(() => { reload(); }, [reload, restaurantId]);

  // Bring the revenue section into view on a `?revenue=1` arrival — it is
  // the LAST of the four sections, and with the others collapsed above it
  // still starts below the fold on most screens. Runs once, after `lines`
  // has actually loaded (the section does not mount at all while empty).
  useEffect(() => {
    if (!arrivedForRevenue || revenueScrolledRef.current) return;
    if (loading || lines.length === 0) return;
    revenueScrolledRef.current = true;
    // Optional call, not just optional access: jsdom (this page's own test
    // suite) has no scrollIntoView at all, and a real browser missing it
    // should degrade to "did not scroll", never a crash.
    revenueSectionRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
  }, [arrivedForRevenue, loading, lines.length]);

  // The coverage strip's jump: re-read the summary AT that period and make
  // sure the sheet is open to edit it.
  const jumpToPeriod = (key) => {
    setPeriodKey(key);
    setTargetsOpen(true);
    loadSummary(key).catch(() => {});
  };

  // A mapping change moves money between lines, so the summary AND the meter
  // both have to be re-read — never patched locally, or the two disagree.
  //
  // Serialised through one chain, because the reads are several full spend
  // scans on a SYNCHRONOUS connection and overlapping them lets an older
  // response land last. But serialised is not the same as DROPPED: this used
  // to `return` early while busy, so a second pick made during a save vanished
  // with no error and no changed row — the user's only clue was that nothing
  // happened. Now each pick waits its turn.
  const chain = useRef(Promise.resolve());
  const onMap = (kind, subjectId, categoryLineId) => {
    setSavingId(subjectId);
    chain.current = chain.current
      .then(async () => {
        try {
          await categoryLinesApi.setMapping(kind, subjectId, categoryLineId);
          await Promise.all([loadMappings(), loadSummary(periodKey), loadLines()]);
        } catch (e) {
          toast(e?.response?.data?.error || 'שגיאה בשמירת השיוך', 'error');
        }
      })
      // Only the last pick in a burst clears the flag, so the spinner does not
      // flicker off between queued saves.
      .finally(() => setSavingId(id => (id === subjectId ? null : id)));
    return chain.current;
  };

  if (!budgetModuleEnabled) {
    return (
      <div>
        <BudgetBreadcrumb />
        <div className="card" style={{ textAlign: 'center', padding: '40px 24px' }}>
          <div style={{ fontWeight: 600, color: 'var(--ink-muted)', marginBottom: 8 }}>
            מודול ההוצאות וההכנסות אינו פעיל למסעדה זו
          </div>
          <div style={{ fontSize: '0.88rem', color: 'var(--ink-muted)' }}>
            הפעלה דרך הגדרות המערכת (אדמין בלבד)
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div>
        <BudgetBreadcrumb />
        <div className="loading-container"><div className="spinner" /></div>
      </div>
    );
  }
  if (loadError) {
    return (
      <div>
        <BudgetBreadcrumb />
        <h1 className="page-title">ניהול קטגוריות</h1>
        {/* NOT an .empty-state. "אין קטגוריות" and "the server is unreachable"
            are different facts, and rendering the first when the second is true
            is how a user concludes their data is gone — the exact bug that put
            InlineLoadError in the codebase. It also carries role="alert" and a
            retry, neither of which an empty state has. */}
        <InlineLoadError message={loadError} onRetry={reload} />
      </div>
    );
  }


  return (
    <div>
      <BudgetBreadcrumb />
      <div className="page-title-row">
        <div className="page-title-group">
          <h1 className="page-title">ניהול קטגוריות</h1>
          {summary?.hidden_hq && <HqOnlyNotice count={summary.hidden_hq.supplier_count} amount={summary.hidden_hq.amount} pending={loading || (periodKey != null && summary.period_key !== periodKey)} basis="רכש בתקופת התקציב (ללא מע״מ)" />}
        </div>
        <PageViewAsMenu />
      </div>

      {/* First run: no lines at all. One action, and a sentence saying what an
          expense line IS — the concept is new even to someone who has used the
          item-type classifications for months. */}
      {lines.length === 0 && (
        <div className="card" style={{ borderInlineStart: '3px solid #2f5c8f' }}>
          <h2 style={{ margin: '0 0 6px', fontSize: 18 }}>אין עדיין קטגוריות</h2>
          <p style={{ color: 'var(--ink-muted)', fontSize: 13.5, margin: '0 0 16px', maxWidth: '62ch' }}>
            קטגוריה מקבץ רכש תחת יעד אחד — מזון, משקאות, תפעול. בדרך כלל 3–10 קטגוריות.
          </p>
          {canEdit ? (
            <button className="btn btn-primary" onClick={() => setDefOpen(true)}>
              הוספת קטגוריה ראשונה
            </button>
          ) : (
            <p style={{ color: '#94a3b8', fontSize: 13 }}>לקביעת תקציב פנו למנהל.</p>
          )}
        </div>
      )}

      {/* 1. Identity. Mounted even with zero lines: this sheet is the ONLY way
          to create a line, so gating it on lines.length would make "הוספת קטגוריה
          ראשון" set state nothing was listening to. */}
      {canEdit && (
        <CategoryLineDefinitionSheet
          lines={lines}
          open={defOpen}
          onToggle={() => setDefOpen(o => !o)}
          onRefresh={() => Promise.all([loadLines(), loadSummary(periodKey), loadMappings()])}
        />
      )}

      {/* 2. What feeds the lines — functionally unchanged. */}
      {lines.length > 0 && canEdit && (
        <BudgetMappingPanel
          lines={lines}
          classifications={mappings?.classifications || []}
          suppliers={mappings?.suppliers || []}
          mappings={mappings}
          onMap={onMap}
          savingId={savingId}
          open={mappingOpen}
          onToggle={() => setMappingOpen(o => !o)}
        />
      )}

      {/* 3. Targets. NO reporting here — spend against budget is answered on
          ניתוח רכש, beside the breakdown that explains it. */}
      {lines.length > 0 && canEdit && summary && settings && (
        <BudgetTargetsSheet
          summary={summary}
          settings={settings}
          open={targetsOpen}
          onToggle={() => setTargetsOpen(o => !o)}
          onClose={() => setTargetsOpen(false)}
          onJumpToPeriod={jumpToPeriod}
          onSaved={() => Promise.all([loadSummary(periodKey), loadSettings()])}
        />
      )}

      {/* 4. Revenue — write for canEdit, read for everyone. The one section a
          viewer gets: it is data entry's home, but also where coverage is
          explained, and a viewer sent here by the widget's banner should not
          meet a blank page. */}
      {lines.length > 0 && (
        <div ref={revenueSectionRef}>
          <RevenueEntrySheet
            canEdit={canEdit}
            vatRate={vatRate}
            open={revenueOpen}
            onToggle={() => setRevenueOpen(o => !o)}
          />
        </div>
      )}

    </div>
  );
}
